import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";

// THOM Verify Africa — continental parent
import AfricaLanding from "@/pages/AfricaLanding";
import AfricaAbout from "@/pages/AfricaAbout";
import AfricaServices from "@/pages/AfricaServices";
import AfricaCountries from "@/pages/AfricaCountries";
import AfricaEnterprise from "@/pages/AfricaEnterprise";
import AfricaIndustries from "@/pages/AfricaIndustries";
import AfricaTrustInfra from "@/pages/AfricaTrustInfra";
import AfricaCountryRequest from "@/pages/AfricaCountryRequest";
import TrackVerification from "@/pages/TrackVerification";
import AdminVerificationRequests from "@/pages/AdminVerificationRequests";
import TrustCentre from "@/pages/TrustCentre";

// THOM Verify Zimbabwe — operational country platform (existing app)
import Landing from "@/pages/Landing";
import Marketplace from "@/pages/Marketplace";
import VehicleDetail from "@/pages/VehicleDetail";
import { Login, Register } from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import VerifyCertificate from "@/pages/VerifyCertificate";
import SellerDashboard from "@/pages/SellerDashboard";
import InspectorDashboard from "@/pages/InspectorDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import BuyerDashboard from "@/pages/BuyerDashboard";
import CommandCentre from "@/pages/CommandCentre";
import PropertyMarketplace from "@/pages/PropertyMarketplace";
import FindMyVehicle from "@/pages/FindMyVehicle";
import BuyerAdvisor from "@/pages/BuyerAdvisor";
import TrustNetwork from "@/pages/TrustNetwork";
import OrganisationProfile from "@/pages/OrganisationProfile";
import OrganisationRegister from "@/pages/OrganisationRegister";
import PartnerPortal from "@/pages/PartnerPortal";
import NetworkIntelligence from "@/pages/NetworkIntelligence";
import DigitalPassport from "@/pages/DigitalPassport";
import ExecutiveOps from "@/pages/ExecutiveOps";
import Services from "@/pages/Services";
import GeographicIntelligence from "@/pages/GeographicIntelligence";
import CountryConfig from "@/pages/CountryConfig";
import EscrowWorkspace from "@/pages/EscrowWorkspace";
import ExecutiveReports from "@/pages/ExecutiveReports";
import VerifiedCertificatePage from "@/pages/VerifiedCertificatePage";
import CertificateAdmin from "@/pages/CertificateAdmin";
import NotificationOps from "@/pages/NotificationOps";
import IllustrativeImages from "@/pages/IllustrativeImages";
import ExecutiveIntelligence from "@/pages/ExecutiveIntelligence";
import SituationRoom from "@/pages/SituationRoom";
import DeveloperPortal from "@/pages/DeveloperPortal";
import VerifyCertificatePortal from "@/pages/VerifyCertificatePortal";
import RevocationRegister from "@/pages/RevocationRegister";
import PartnerWorkspace from "@/pages/PartnerWorkspace";
import MarketplaceAdmin from "@/pages/MarketplaceAdmin";
import AfricaCountryLanding from "@/pages/AfricaCountryLanding";
import EscrowAccept from "@/pages/EscrowAccept";
import PartnerOnboarding from "@/pages/PartnerOnboarding";
import OperationsCommandCentre from "@/pages/OperationsCommandCentre";

const AppRouter = () => {
  const location = useLocation();
  // Handle Emergent OAuth callback (hash param) at any route
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* ─── THOM Verify Africa — continental parent site ────────────────── */}
      <Route path="/"                               element={<AfricaLanding />} />
      <Route path="/africa"                         element={<AfricaLanding />} />
      <Route path="/africa/about"                   element={<AfricaAbout />} />
      <Route path="/africa/services"                element={<AfricaServices />} />
      <Route path="/africa/countries"               element={<AfricaCountries />} />
      <Route path="/africa/enterprise"              element={<AfricaEnterprise />} />
      <Route path="/africa/industries"              element={<AfricaIndustries />} />
      <Route path="/africa/trust-infrastructure"    element={<AfricaTrustInfra />} />
      <Route path="/africa/countries/:iso"          element={<AfricaCountryLanding />} />
      <Route path="/africa/countries/:iso/request"  element={<AfricaCountryRequest />} />
      <Route path="/escrow/accept"                  element={<EscrowAccept />} />
      <Route path="/africa/contact"                 element={<AfricaEnterprise />} />
      <Route path="/track-verification"             element={<TrackVerification />} />
      <Route path="/trust-centre"                    element={<TrustCentre />} />
      <Route path="/trust-center"                    element={<TrustCentre />} />
      <Route path="/admin/verification-requests"    element={<ProtectedRoute roles={["admin","super_admin","country_admin","ops_manager","executive"]}><AdminVerificationRequests /></ProtectedRoute>} />
      <Route path="/countries/zimbabwe"             element={<Navigate to="/zw" replace />} />

      {/* ─── THOM Verify Zimbabwe — country platform (existing app) ──────── */}
      <Route path="/zw"                             element={<Landing />} />
      <Route path="/zw/how-it-works"                element={<Landing />} />

      {/* Preserved existing routes (Zimbabwe deployment surfaces) */}
      <Route path="/marketplace"                    element={<Marketplace />} />
      <Route path="/vehicles/:id"                   element={<VehicleDetail />} />
      <Route path="/login"                          element={<Login />} />
      <Route path="/register"                       element={<Register />} />
      <Route path="/how-it-works"                   element={<Landing />} />
      <Route path="/verify-legacy"                  element={<VerifyCertificate />} />
      <Route path="/verify-legacy/:certNumber"      element={<VerifyCertificate />} />
      <Route path="/dashboard"                      element={<AuthCallback />} />
      <Route path="/buyer"                          element={<ProtectedRoute roles={["buyer","admin"]}><BuyerDashboard /></ProtectedRoute>} />
      <Route path="/seller"                         element={<ProtectedRoute roles={["seller","dealer","admin"]}><SellerDashboard /></ProtectedRoute>} />
      <Route path="/inspector"                      element={<ProtectedRoute roles={["inspector","admin"]}><InspectorDashboard /></ProtectedRoute>} />
      <Route path="/admin"                          element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/command-centre"                 element={<ProtectedRoute roles={["admin"]}><CommandCentre /></ProtectedRoute>} />
      <Route path="/property"                       element={<PropertyMarketplace />} />
      <Route path="/find-my-vehicle"                element={<FindMyVehicle />} />
      <Route path="/advisor"                        element={<BuyerAdvisor />} />
      <Route path="/network"                        element={<TrustNetwork />} />
      <Route path="/network/register"               element={<ProtectedRoute><OrganisationRegister /></ProtectedRoute>} />
      <Route path="/network/partner"                element={<ProtectedRoute><PartnerPortal /></ProtectedRoute>} />
      <Route path="/network/intelligence"           element={<ProtectedRoute roles={["admin"]}><NetworkIntelligence /></ProtectedRoute>} />
      <Route path="/network/:id"                    element={<OrganisationProfile />} />
      <Route path="/passport/:assetType/:assetId"   element={<DigitalPassport />} />
      <Route path="/geo-intelligence"               element={<ProtectedRoute roles={["admin"]}><GeographicIntelligence /></ProtectedRoute>} />
      <Route path="/ops"                            element={<ProtectedRoute roles={["admin","super_admin","executive","country_admin","ops_manager","finance_manager"]}><ExecutiveOps /></ProtectedRoute>} />
      <Route path="/admin/countries"                element={<ProtectedRoute roles={["admin","super_admin","country_admin","executive"]}><CountryConfig /></ProtectedRoute>} />
      <Route path="/admin/escrow"                   element={<ProtectedRoute roles={["admin","super_admin","country_admin","ops_manager","finance_manager","executive","inspector"]}><EscrowWorkspace /></ProtectedRoute>} />
      <Route path="/admin/reports"                  element={<ProtectedRoute roles={["admin","super_admin","country_admin","executive","finance_manager"]}><ExecutiveReports /></ProtectedRoute>} />
      <Route path="/admin/certificates"             element={<ProtectedRoute roles={["admin","super_admin","country_admin","ops_manager","executive"]}><CertificateAdmin /></ProtectedRoute>} />
      <Route path="/admin/notifications"            element={<ProtectedRoute roles={["admin","super_admin","country_admin","ops_manager","finance_manager","executive"]}><NotificationOps /></ProtectedRoute>} />
      <Route path="/admin/illustrative-images"      element={<ProtectedRoute roles={["admin","super_admin","country_admin","ops_manager","executive","finance_manager"]}><IllustrativeImages /></ProtectedRoute>} />
      <Route path="/admin/intelligence"             element={<ProtectedRoute roles={["admin","super_admin","country_admin","ops_manager","executive","finance_manager"]}><ExecutiveIntelligence /></ProtectedRoute>} />
      <Route path="/admin/situation-room"           element={<ProtectedRoute roles={["admin","super_admin","country_admin","ops_manager","executive","finance_manager"]}><SituationRoom /></ProtectedRoute>} />
      <Route path="/admin/api"                      element={<ProtectedRoute roles={["admin","super_admin","executive"]}><DeveloperPortal /></ProtectedRoute>} />
      <Route path="/verify/certificate"             element={<VerifyCertificatePortal />} />
      <Route path="/verify/certificate/:certNumber" element={<VerifyCertificatePortal />} />
      <Route path="/verify/revocation-register"     element={<RevocationRegister />} />
      <Route path="/orgs"                           element={<ProtectedRoute><PartnerWorkspace /></ProtectedRoute>} />
      <Route path="/orgs/:orgId"                    element={<ProtectedRoute><PartnerWorkspace /></ProtectedRoute>} />
      <Route path="/partners/onboard"               element={<ProtectedRoute><PartnerOnboarding /></ProtectedRoute>} />
      <Route path="/partners/onboarding"            element={<ProtectedRoute><PartnerOnboarding /></ProtectedRoute>} />
      <Route path="/admin/command-centre"           element={<ProtectedRoute><OperationsCommandCentre /></ProtectedRoute>} />
      <Route path="/command-centre"                 element={<ProtectedRoute><OperationsCommandCentre /></ProtectedRoute>} />
      <Route path="/admin/marketplace"              element={<ProtectedRoute roles={["admin","super_admin","country_admin","ops_manager","executive"]}><MarketplaceAdmin /></ProtectedRoute>} />
      <Route path="/verify/:certNumber"             element={<VerifiedCertificatePage />} />
      <Route path="/verify"                         element={<VerifiedCertificatePage />} />
      <Route path="/services"                       element={<Services />} />

      {/* /zw/* aliases for future subdomain migration (zw.thomverify.africa/*) */}
      <Route path="/zw/marketplace"                 element={<Marketplace />} />
      <Route path="/zw/property"                    element={<PropertyMarketplace />} />
      <Route path="/zw/find-my-vehicle"             element={<FindMyVehicle />} />
      <Route path="/zw/advisor"                     element={<BuyerAdvisor />} />
      <Route path="/zw/services"                    element={<Services />} />
      <Route path="/zw/network"                     element={<TrustNetwork />} />
      <Route path="/zw/verify"                      element={<VerifyCertificate />} />

      {/* Any unknown path — route to Africa parent, not Zimbabwe landing */}
      <Route path="*"                               element={<AfricaLanding />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
